import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, downloadFile, formatApiErrorDetail } from "../lib/api";
import FilePicker from "../components/FilePicker";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const TABS = ["chat", "summarize", "extract", "translate"];

export default function AiStudio() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [file, setFile] = useState(null); // {id, original_filename, ext, ...}
  const [tab, setTab] = useState("chat");

  // Chat state
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  // Other tabs results
  const [summary, setSummary] = useState(null);
  const [entities, setEntities] = useState(null);
  const [targetLang, setTargetLang] = useState("English");
  const [translated, setTranslated] = useState(null);

  // Auto-select file from ?file_id (e.g. from Dashboard quick-upload)
  useEffect(() => {
    const fid = searchParams.get("file_id");
    if (fid && !file) {
      api.get("/files").then(({ data }) => {
        const found = data.find((f) => f.id === fid);
        if (found) setFile(found);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  function reset() {
    setSessionId(null);
    setMessages([]);
    setSummary(null);
    setEntities(null);
    setTranslated(null);
  }

  async function send() {
    if (!file || !input.trim()) return;
    setBusy(true);
    const userMsg = { role: "user", content: input };
    setMessages((m) => [...m, userMsg]);
    const text = input;
    setInput("");
    try {
      const { data } = await api.post("/ai/chat", {
        file_id: file.id,
        message: text,
        session_id: sessionId,
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setMessages((m) => m.slice(0, -1));
    }
    setBusy(false);
  }

  async function doSummarize() {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/summarize", {
        file_id: file.id,
        language: i18n.language,
      });
      setSummary(data);
      toast.success(t("success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }

  async function doExtract() {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/extract", { file_id: file.id });
      setEntities(data.entities);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }

  async function doTranslate() {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/translate", {
        file_id: file.id,
        target_language: targetLang,
      });
      setTranslated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-12" data-testid="ai-studio-page">
      <Link to="/tools/pdf" className="eyebrow text-zinc-500 hover:text-black">← {t("cta_back")}</Link>
      <h1 className="hero-h2 text-3xl sm:text-5xl mt-2">{t("tool_ai_studio")}</h1>
      <p className="text-zinc-500 mt-2">{t("tool_ai_studio_desc")}</p>

      <div className="mt-8">
        {!file ? (
          <FilePicker accept="" onSelected={(f) => { setFile(f); reset(); }} />
        ) : (
          <>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
              <div>
                <span className="eyebrow">FILE</span>
                <div className="hero-h2 text-xl mt-1">{file.original_filename}</div>
              </div>
              <Button variant="ghost" className="rounded-sm" onClick={() => { setFile(null); reset(); }}
                data-testid="ai-clear-file">
                {t("cta_clear")}
              </Button>
            </div>

            <div className="border border-zinc-200 rounded-md overflow-hidden">
              <div className="flex border-b border-zinc-200 bg-zinc-50">
                {TABS.map((tk) => (
                  <button
                    key={tk}
                    className={`px-5 py-3 text-xs uppercase tracking-wider font-medium ${tab === tk ? "bg-white border-b-2 border-black -mb-px" : "text-zinc-500 hover:text-black"}`}
                    onClick={() => setTab(tk)}
                    data-testid={`ai-tab-${tk}`}
                  >
                    {t(`ai_${tk === "chat" ? "chat_title" : tk}`)}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {tab === "chat" && (
                  <div className="grid gap-4">
                    <div ref={scrollRef} className="border border-zinc-200 rounded-sm p-4 h-[420px] overflow-auto bg-zinc-50 space-y-3"
                      data-testid="ai-chat-window">
                      {messages.length === 0 && (
                        <div className="text-zinc-500 text-sm">{t("ai_chat_placeholder")}</div>
                      )}
                      {messages.map((m, i) => (
                        <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[80%] px-4 py-3 rounded-md text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-black text-white" : "bg-white border border-zinc-200"}`}>
                            {m.content}
                          </div>
                        </div>
                      ))}
                      {busy && <div className="text-zinc-500 text-sm">{t("ai_thinking")}</div>}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !busy) send(); }}
                        placeholder={t("ai_chat_placeholder")}
                        className="rounded-sm border-zinc-300 h-11"
                        data-testid="ai-chat-input"
                      />
                      <Button onClick={send} disabled={busy || !input.trim()}
                        className="rounded-sm bg-black text-white hover:bg-zinc-800 h-11"
                        data-testid="ai-chat-send">
                        →
                      </Button>
                    </div>
                  </div>
                )}

                {tab === "summarize" && (
                  <div>
                    <Button onClick={doSummarize} disabled={busy}
                      className="rounded-sm bg-black text-white hover:bg-zinc-800 h-11"
                      data-testid="ai-summarize-btn">
                      {busy ? t("processing") : `${t("ai_summarize")} →`}
                    </Button>
                    {summary && (
                      <div className="mt-6 border border-black rounded-md p-6 bg-zinc-50 fade-up" data-testid="ai-summary-result">
                        <span className="eyebrow">{t("result_ready")}</span>
                        <div className="hero-h2 text-xl mt-1">{summary.original_filename}</div>
                        <Button onClick={() => downloadFile(summary.id, summary.original_filename)}
                          className="rounded-sm bg-black text-white hover:bg-zinc-800 h-10 mt-4">
                          ↓ {t("cta_download")}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {tab === "extract" && (
                  <div>
                    <Button onClick={doExtract} disabled={busy}
                      className="rounded-sm bg-black text-white hover:bg-zinc-800 h-11"
                      data-testid="ai-extract-btn">
                      {busy ? t("processing") : `${t("ai_extract")} →`}
                    </Button>
                    {entities && (
                      <pre className="mt-6 bg-zinc-50 border border-zinc-200 rounded-md p-4 text-xs overflow-auto max-h-[500px] mono"
                        data-testid="ai-extract-result">
                        {JSON.stringify(entities, null, 2)}
                      </pre>
                    )}
                  </div>
                )}

                {tab === "translate" && (
                  <div className="grid gap-4">
                    <div className="flex gap-3 items-end">
                      <div className="flex-1 max-w-xs">
                        <span className="eyebrow">{t("ai_target_lang")}</span>
                        <Input value={targetLang} onChange={(e) => setTargetLang(e.target.value)}
                          className="rounded-sm border-zinc-300 h-11 mt-1" data-testid="ai-translate-lang" />
                      </div>
                      <Button onClick={doTranslate} disabled={busy}
                        className="rounded-sm bg-black text-white hover:bg-zinc-800 h-11"
                        data-testid="ai-translate-btn">
                        {busy ? t("processing") : `${t("ai_translate")} →`}
                      </Button>
                    </div>
                    {translated && (
                      <div className="border border-black rounded-md p-6 bg-zinc-50 fade-up" data-testid="ai-translate-result">
                        <span className="eyebrow">{t("result_ready")}</span>
                        <div className="hero-h2 text-xl mt-1">{translated.original_filename}</div>
                        <Button onClick={() => downloadFile(translated.id, translated.original_filename)}
                          className="rounded-sm bg-black text-white hover:bg-zinc-800 h-10 mt-4">
                          ↓ {t("cta_download")}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
